import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Field, FieldProps, Formik } from 'formik'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IoSettingsOutline } from 'react-icons/io5'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { colors } from '../../../ui-kit'
import useToast from '../../hooks/useToast'
import {
  CreateBookmarkErrorResponse,
  CreateBookmarkSuccessResponse,
} from '../../interfaces'
import { UserContext } from '../../providers'

interface Props {
  bookmarkId: string
  isOpen: boolean
  name: string
  link: string
  onClose(): void
  getAndSetBookmarks(): void
}

interface FormProps {
  name: string
  link: string
}

const ManageBookmarkModal = ({
  bookmarkId,
  name,
  link,
  isOpen,
  onClose,
  getAndSetBookmarks,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [formError, setFormError] = useState<string>('')
  const { user } = useContext(UserContext)
  const toast = useToast()
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const deleteBookmark = async () => {
    setFormError('')
    const res = (await request.delete(
      `/api/media-library/${user?.clientNumber}/bookmarks/${bookmarkId}`,
    )) as { bookmarkId: string } | CreateBookmarkErrorResponse

    if (request.error) {
      setFormError(
        (res as CreateBookmarkErrorResponse).message ??
          t(
            'error_in_deleting_bookmark',
            'Error in deleting bookmark, please try again later.',
          ),
      )
    } else {
      toast({
        description: t(
          'bookmark_deleted_successfully',
          'Bookmark deleted successfully.',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
      })
      setFormError('')
      void getAndSetBookmarks()
      onClose()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
        setFormError('')
      }}
      size="xl"
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          enableReinitialize
          initialValues={{
            name: name ?? '',
            link: link ?? '',
          }}
          validationSchema={Yup.object().shape({
            name: Yup.string().required('Required'),
            link: Yup.string()
              .matches(
                /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/,
                'Enter a valid URL',
              )
              .required('Required')
              .max(2000, 'Link cannot exceed 2000 characters.'),
          })}
          onSubmit={async values => {
            setFormError('')
            const res = (await request.put(
              `/api/media-library/${user?.clientNumber}/bookmarks/${bookmarkId}`,
              values,
            )) as CreateBookmarkSuccessResponse | CreateBookmarkErrorResponse

            if (request.error) {
              setFormError(
                (res as CreateBookmarkErrorResponse).message ??
                  t(
                    'error_in_updating_bookmark',
                    'Error in updating bookmark, please try again later.',
                  ),
              )
            } else {
              toast({
                description: t(
                  'bookmark_updated_successfully',
                  'Bookmark updated successfully.',
                ),
                status: 'success',
                isClosable: true,
                duration: 5000,
                position: 'top-right',
              })
              setFormError('')
              void getAndSetBookmarks()
              onClose()
            }
          }}
        >
          {({ submitForm, errors, touched, isSubmitting, dirty, isValid }) => (
            <>
              <ModalHeader>
                <Icon
                  as={IoSettingsOutline}
                  color="appBattleshipGrey"
                  backgroundColor="appPaleGrey"
                  boxSize={6}
                  padding="0.1em"
                  borderRadius="full"
                  marginRight="0.5em"
                />
                {t('manage_bookmark', 'Manage bookmark')}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {formError ? (
                  <Alert status="error" mb="14px">
                    <AlertIcon />
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                ) : null}
                <Field name="name">
                  {({ field }: FieldProps<string, FormProps>) => (
                    <FormControl
                      isRequired
                      isInvalid={Boolean(errors.name && touched.name)}
                      mb={4}
                    >
                      <FormLabel>{t('name', 'Name')}</FormLabel>
                      <Input
                        {...field}
                        placeholder={t(
                          'bookmark_name_placeholder',
                          'Enter bookmark name',
                        )}
                      />
                      {errors.name && touched.name ? (
                        <FormErrorMessage>{errors.name}</FormErrorMessage>
                      ) : null}
                    </FormControl>
                  )}
                </Field>
                <Field name="link">
                  {({ field }: FieldProps<string, FormProps>) => (
                    <FormControl
                      isRequired
                      isInvalid={Boolean(errors.link && touched.link)}
                    >
                      <FormLabel>{t('link', 'Link')}</FormLabel>
                      <Input
                        {...field}
                        placeholder={t(
                          'bookmark_link_placeholder',
                          'Enter bookmark URL',
                        )}
                      />
                      {errors.link && touched.link ? (
                        <FormErrorMessage>{errors.link}</FormErrorMessage>
                      ) : null}
                    </FormControl>
                  )}
                </Field>
              </ModalBody>
              <ModalFooter
                borderTop={`1px solid ${colors.appGrey}`}
                style={{ gap: '1em' }}
              >
                <Popover placement="top">
                  {({ onClose: popoverOnClose }) => (
                    <>
                      <PopoverTrigger>
                        <Button
                          colorScheme="appBrick"
                          onClick={e => {
                            e.stopPropagation()
                          }}
                        >
                          {t('delete', 'Delete')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent onClick={e => e.stopPropagation()}>
                        <PopoverArrow />
                        <PopoverCloseButton color="appSlate" />
                        <PopoverHeader color="appSlate" fontWeight="600">
                          {t('delete_bookmark', 'Delete bookmark')}
                        </PopoverHeader>
                        <PopoverBody color="appSlate">
                          <VStack
                            spacing="4"
                            justifyContent="flex-start"
                            width="full"
                            alignItems="flex-start"
                          >
                            <Text fontWeight="500">
                              {t(
                                'delete_confirmation_message',
                                'Are you sure you want to delete?',
                              )}
                            </Text>
                            <Button
                              colorScheme="appBrick"
                              onClick={async () => {
                                await deleteBookmark()
                                popoverOnClose()
                              }}
                            >
                              {t('confirm', 'Confirm')}
                            </Button>
                          </VStack>
                        </PopoverBody>
                      </PopoverContent>
                    </>
                  )}
                </Popover>
                <Spacer />
                <Button variant="outline" onClick={onClose}>
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  colorScheme="appBlue"
                  onClick={async () => await submitForm()}
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting || !dirty || !isValid}
                >
                  {t('submit', 'Submit')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default ManageBookmarkModal
